import { useEffect, useState } from "react";
import { AlertTriangle, Radio } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDict } from "@/lib/i18n/context";
import { INTERVIEW } from "@/lib/constants";

type Live = {
  status: string;
  violationCount: number;
  answeredCount: number;
  events: { type: string; payload: unknown; created_at: string }[];
  liveUrl: string | null;
  snapshotUrl: string | null;
  snapshotAt: string | null;
};

export function LiveView({
  interviewId,
  totalQuestions,
}: {
  interviewId: string;
  totalQuestions: number;
}) {
  const t = useDict();
  const eventLabels = t.live.events;
  const [live, setLive] = useState<Live | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let active = true;
    async function poll() {
      try {
        const res = await fetch(`/api/interviews/${interviewId}/live`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data: Live = await res.json();
        if (!active) return;
        setLive(data);
        setTick(Date.now());
        if (data.status === "completed" || data.status === "violated") {
          window.location.reload();
        }
      } catch {
        /* ignore */
      }
    }
    poll();
    const id = setInterval(poll, INTERVIEW.LIVE_POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [interviewId]);

  const frameSrc = live?.liveUrl
    ? `${live.liveUrl}?t=${tick}`
    : live?.snapshotUrl ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-semibold">
              <Radio className="h-4 w-4 text-red-500" /> {t.live.liveCamera}
            </h2>
            <Badge tone="blue">
              {live?.answeredCount ?? 0}/{totalQuestions} {t.live.answered}
            </Badge>
          </div>
          <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-slate-900">
            {frameSrc ? (
              <img
                key="live"
                src={frameSrc}
                alt="Live view"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted">
                {t.live.waitingCamera}
              </div>
            )}
            <div className="absolute start-3 top-3 flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs font-medium text-white">
              <span className="recording-dot h-2 w-2 rounded-full bg-card" />
              {t.live.live}
            </div>
          </div>
          {live &&
            live.status === "in_progress" &&
            live.answeredCount >= totalQuestions &&
            totalQuestions > 0 && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-700">
                <span className="recording-dot h-2 w-2 rounded-full bg-brand-500" />
                {t.live.evaluating}
              </div>
            )}
          {live && live.violationCount > 0 && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              {live.violationCount} {t.live.violations}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h2 className="mb-3 font-semibold">{t.live.activity}</h2>
          <div className="space-y-2">
            {(live?.events ?? []).map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    e.type === "violation" ? "bg-red-500" : "bg-brand-500"
                  }`}
                />
                <div>
                  <p>{eventLabels[e.type] ?? e.type}</p>
                  <p className="text-xs text-muted">
                    {new Date(e.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {(!live || live.events.length === 0) && (
              <p className="text-sm text-muted">{t.live.noActivity}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
